import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Body2, Heading4 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { PageSection } from '@ui/components/PageSection'
import { Tab } from '@ui/components/Tabs/Tabs'
import { Tooltip } from '@ui/components/Tooltip'
import { ChartIcon } from '@ui/icons/Chart.icon'
import { NewClockIcon } from '@ui/icons/ClockNew.icon'
import { GiftIcon } from '@ui/icons/Gift.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useWindowResize } from '@utils/domUtils'

import { CampaignsAvailableToJoinDto } from '@model/RewardsHubDashboardDto'

import { useActiveTab } from '@hooks/useActiveTab'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { ActiveRewards } from './ActiveReward'
import { AvailableReward, RewardTypeEnum } from './AvailableReward'
import { useCampaignsHistory } from './hooks/useCampaignsHistory'
import { useRewardsHubDashboard } from './hooks/useRewardsHubDashboard'

const TRADING_VIEW_PLACEHOLDER_CAMPAIGN_ID = 'trading-view-placeholder'

const createTradingViewPlaceholder = (): CampaignsAvailableToJoinDto => ({
  campaign: { id: TRADING_VIEW_PLACEHOLDER_CAMPAIGN_ID, name: '' },
  type: {
    id: RewardTypeEnum.TradingView,
    name: RewardTypeEnum[RewardTypeEnum.TradingView],
  },
  joinDate: '',
  description: '',
  termsAndConditionsUrl: '',
  tradingPool: { currentPrizePool: 0, drawDate: '' },
  startDate: '',
})

export const RewardsHubPage = () => {
  const { t } = useTranslation()
  const { rewardsHubDashboard, isLoading } = useRewardsHubDashboard()
  const [isHistoryFilterVisible, setIsHistoryFilterVisible] = useState(false)

  const { campaignsHistory: allCampaignsHistory } = useCampaignsHistory()
  const hasCampaignHistory = allCampaignsHistory.length > 0

  const activeRewardsTabs: Tab[] = [
    {
      id: 'trading-tasks',
      label: t('RewardsHub.Trading Tasks'),
    },
  ]

  const historyTabs: Tab[] = useMemo(() => {
    const tabs: Tab[] = [
      {
        id: 'rewards',
        path: 'rewards',
        label: t('RewardsHub.Rewards'),
      },
    ]
    if (hasCampaignHistory) {
      tabs.push({
        id: 'campaigns',
        path: 'campaigns',
        label: t('RewardsHub.Campaigns'),
      })
    }
    return tabs
  }, [t, hasCampaignHistory])

  const { activeTab: activeHistoryTab } = useActiveTab(historyTabs, {
    includeNested: false,
    initialIndex: 0,
  })

  const rewardsToJoin = useMemo((): CampaignsAvailableToJoinDto[] => {
    if (!rewardsHubDashboard) return []

    const campaigns = [...rewardsHubDashboard.campaignsAvailableToJoin]
    const hasTradingViewCampaign = campaigns.some(
      ({ type }) => type.id === RewardTypeEnum.TradingView
    )

    const shouldInjectTradingViewPlaceholder =
      rewardsHubDashboard.campaignsInProgress.tradingViewCouponReward === null &&
      !hasTradingViewCampaign

    if (shouldInjectTradingViewPlaceholder) {
      campaigns.push(createTradingViewPlaceholder())
    }

    return campaigns
  }, [rewardsHubDashboard])

  return (
    <Loading isLoading={isLoading}>
      <Box flex direction='column' gap='x32'>
        <PageSection title={t('RewardsHub.Rewards Hub')}></PageSection>
        <RewardsHubDashboard
          currentMonthLots={rewardsHubDashboard?.monthlyTradedVolume ?? 0}
          availabelCampaignToJoin={rewardsHubDashboard?.availableToJoin ?? 0}
          participatingCampaigns={rewardsHubDashboard?.trackingProgress ?? 0}
          rewardsToClaim={rewardsHubDashboard?.rewardsToClaim ?? 0}
        />
        <Box flex direction='column' gap='x24'>
          <PageSection title={t('RewardsHub.Unlock your rewards')} icon={GiftIcon}></PageSection>
          <AvailableReward
            campaigns={rewardsToJoin}
            rewardsToClaim={rewardsHubDashboard?.rewardsToClaim ?? 0}
          />
        </Box>

        <Box flex direction='column' gap='x24'>
          <PageSection
            title={t('RewardsHub.Progression')}
            icon={ChartIcon}
            tabData={{ tabs: activeRewardsTabs }}
          />
          <ActiveRewards rewardsHubDashboard={rewardsHubDashboard} />
        </Box>

        <Box flex direction='column' gap='x32'>
          <PageSection
            title={t('RewardsHub.History')}
            icon={NewClockIcon}
            tabData={{ tabs: historyTabs }}
            {...(hasCampaignHistory
              ? {
                  onFilterClick: () => setIsHistoryFilterVisible((visible) => !visible),
                  isFilterButtonDisabled: activeHistoryTab?.id !== 'campaigns',
                }
              : {})}
          />
          <Outlet context={{ isHistoryFilterVisible }} />
        </Box>
      </Box>
    </Loading>
  )
}

interface RewardsHubHistoryOutletContext {
  isHistoryFilterVisible: boolean
}

export const useRewardsHubHistoryOutletContext = (): RewardsHubHistoryOutletContext => {
  return useOutletContext<RewardsHubHistoryOutletContext>()
}

interface RewardsHubDashboardProps {
  currentMonthLots: number
  availabelCampaignToJoin: number
  participatingCampaigns: number
  rewardsToClaim: number
}
const RewardsHubDashboard: FC<RewardsHubDashboardProps> = ({
  currentMonthLots,
  availabelCampaignToJoin,
  participatingCampaigns,
  rewardsToClaim,
}: RewardsHubDashboardProps) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const dashboardItems = [
    {
      key: 'lots',
      value: `${currentMonthLots} ${t('RewardsHub.lots')}`,
      label: t('RewardsHub.Current Month Volume'),
      tooltip: (
        <Tooltip heading={t('RewardsHub.Reward(s) to Claim')}>
          <ActionIcon size='x24' icon={InfoIcon} />
        </Tooltip>
      ),
      showTooltip: !isMobile,
    },
    {
      key: 'available',
      value: availabelCampaignToJoin,
      label: t('RewardsHub.Available to Join'),
    },
    {
      key: 'participating',
      value: participatingCampaigns,
      label: t('RewardsHub.Tracking Progress'),
    },
    {
      key: 'rewards',
      value: rewardsToClaim,
      label: isMobile ? t('RewardsHub.Reward(s) to Claim') : t('RewardsHub.Reward(s) to Claim'),
    },
  ]

  interface DashboardItemProps {
    value: string | number
    label: string
    tooltip?: React.ReactNode
    showTooltip?: boolean
    key: string
  }

  const DashboardItem = ({ value, label, tooltip, showTooltip, key }: DashboardItemProps) => (
    <Box flex direction='column' align='center' key={key} fullWidth>
      <Box flex gap='x8' align='center'>
        <Heading4 semibold isParagraph>
          {value}
        </Heading4>
        {showTooltip && tooltip}
      </Box>
      <Body2 color='text-med-em' isParagraph>
        {label}
      </Body2>
    </Box>
  )

  if (isMobile) {
    return (
      <Box flex direction='column' gap='x16' my='x20'>
        <Box flex justify='space-between'>
          <DashboardItem {...dashboardItems[0]} />
          <DashboardItem {...dashboardItems[1]} />
        </Box>
        <Box flex justify='space-between'>
          <DashboardItem {...dashboardItems[2]} />
          <DashboardItem {...dashboardItems[3]} />
        </Box>
      </Box>
    )
  }

  return (
    <Box flex justify='space-between' mt='x24'>
      {dashboardItems.map((item) => (
        <DashboardItem {...item} key={item.key} />
      ))}
    </Box>
  )
}
